'use client';

import { useState } from 'react';

import { useAuth } from '@clerk/nextjs';
import { datadogLogs } from '@datadog/browser-logs';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { wsLink, createWSClient, splitLink } from '@trpc/client';
import {
  httpBatchLink,
  loggerLink,
  createTRPCReact,
  retryLink,
} from '@trpc/react-query';
import superjson from 'superjson';

import type { AppRouter } from '@avodah-inc/trpc-gateway';

import { env } from '~/env.mjs';

/* eslint-disable react/hook-use-state -- we don't need state setters for this file */

export const api = createTRPCReact<AppRouter>({});

const getAuthorizationHeader = async (
  getToken: ReturnType<typeof useAuth>['getToken'],
) => {
  const t = await getToken({ skipCache: false, template: 'default' });
  if (!t) return '';
  return `Bearer ${t}`;
};

export const TRPCReactProvider = (props: {
  children: React.ReactNode;
  cookies: string;
}) => {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // With SSR, we usually want to set some default staleTime
            // above 0 to avoid refetching immediately on the client
            staleTime: 3_000,
          },
        },
      }),
  );
  const { getToken } = useAuth();
  const [trpcClient] = useState(() =>
    api.createClient({
      links: [
        loggerLink({
          enabled: (op) =>
            process.env.NODE_ENV === 'development' ||
            (op.direction === 'down' && op.result instanceof Error),
        }),
        splitLink({
          condition: (op) => op.type === 'subscription',
          true: wsLink({
            client: createWSClient({
              url: env.NEXT_PUBLIC_TRPC_GATEWAY_URL.replace('http', 'ws'),
              connectionParams: async () => ({
                token: (await getToken()) ?? '',
              }),
            }),
            transformer: superjson,
          }),
          false: httpBatchLink({
            url: env.NEXT_PUBLIC_TRPC_GATEWAY_URL,
            async headers() {
              const authorizationToken = await getAuthorizationHeader(getToken);
              datadogLogs.logger.info('trpc-client-invoker', {
                authorizationToken,
              });
              return {
                credentials: 'include',
                cache: 'no-store',
                Authorization: authorizationToken,
              };
            },
            transformer: superjson,
          }),
        }),
        retryLink({
          retry(opts) {
            if (
              opts.error.data &&
              opts.error.data.code !== 'INTERNAL_SERVER_ERROR'
            ) {
              // Don't retry on non-500s
              return false;
            }
            if (opts.op.type !== 'query') {
              // Only retry queries
              return false;
            }
            // Retry up to 3 times
            return opts.attempts <= 3;
          },
        }),
      ],
    }),
  );

  return (
    <api.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        {props.children}
      </QueryClientProvider>
    </api.Provider>
  );
};
